/** API client for the public status API and the admin API.
 *
 * Both APIs have stable shapes; see docs/adrs/009-public-api-contract.md.
 * The base URLs come from Vite env vars so the same bundle can be built
 * for dev and prd.
 */

const PUBLIC_API =
  import.meta.env.VITE_PUBLIC_API_BASE_URL ?? "/v1";
// Admin API lives at /api/* so it does not collide with the SPA's
// client-side /admin/* React Router routes. CloudFront routes /api/*
// to the admin API Gateway; everything else goes to the SPA.
const ADMIN_API =
  import.meta.env.VITE_ADMIN_API_BASE_URL ?? "/api";

export type ComponentStatus =
  | "operational"
  | "degraded"
  | "partial_outage"
  | "major_outage"
  | "maintenance";

export interface Component {
  component_id: string;
  name: string;
  description?: string;
  group?: string;
  kind?: "internal" | "upstream";
  status: ComponentStatus;
  status_reason?: string;
  updated_at?: string;
}

export interface StatusResponse {
  overall_status: ComponentStatus;
  components: {
    core: Component[];
    upstream: Component[];
  };
}

export interface IncidentUpdate {
  version: string;
  status: string;
  body_markdown: string;
  posted_by: string;
}

export interface Incident {
  incident_id: string;
  title: string;
  severity: string;
  status: string;
  affected_components: string[];
  started_at?: string;
  resolved_at?: string;
  is_maintenance: boolean;
  scheduled_for?: string;
  posted_by?: string;
  updates: IncidentUpdate[];
}

export interface IncidentsResponse {
  incidents: Incident[];
}

export interface UptimeRow {
  component_id: string;
  name: string;
  uptime_percent: number;
  window: string;
}

export interface UptimeResponse {
  window: string;
  window_seconds: number;
  computed_at: string;
  components: UptimeRow[];
}

export interface UptimeDayPoint {
  date: string;
  uptime_percent: number;
  worst_state: ComponentStatus;
  incident_ids: string[];
}

export interface UptimeDailyComponent {
  component_id: string;
  name: string;
  days: UptimeDayPoint[];
}

export interface UptimeDailyResponse {
  resolution: "daily";
  days: number;
  computed_at: string;
  components: UptimeDailyComponent[];
}

async function getJSON<T>(url: string): Promise<T> {
  const resp = await fetch(url, {
    headers: { accept: "application/json" },
    cache: "no-store",
  });
  if (!resp.ok) {
    throw new Error(`GET ${url} failed: ${resp.status}`);
  }
  return (await resp.json()) as T;
}

export const publicApi = {
  status: () => getJSON<StatusResponse>(`${PUBLIC_API}/status`),
  activeIncidents: () =>
    getJSON<IncidentsResponse>(`${PUBLIC_API}/incidents?status=active`),
  resolvedIncidents: () =>
    getJSON<IncidentsResponse>(`${PUBLIC_API}/incidents?status=resolved`),
  archive: (year: string, month: string) =>
    getJSON<IncidentsResponse>(
      `${PUBLIC_API}/incidents/archive?year=${encodeURIComponent(year)}&month=${encodeURIComponent(month)}`,
    ),
  upcomingMaintenance: () =>
    getJSON<IncidentsResponse>(`${PUBLIC_API}/maintenance/upcoming`),
  uptime: (window: "30d" | "60d" | "90d") =>
    getJSON<UptimeResponse>(`${PUBLIC_API}/uptime?window=${window}`),
  uptimeDaily: (days = 90) =>
    getJSON<UptimeDailyResponse>(
      `${PUBLIC_API}/uptime?resolution=daily&days=${days}`,
    ),
};

// ---------------------------------------------------------------------------
// Admin API
// ---------------------------------------------------------------------------

const TOKEN_KEY = "status_access_token";

export const session = {
  get(): string | null {
    try {
      return window.localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  },
  set(token: string) {
    window.localStorage.setItem(TOKEN_KEY, token);
  },
  clear() {
    try {
      window.localStorage.removeItem(TOKEN_KEY);
    } catch {
      /* no-op */
    }
  },
};

async function authedRequest<T>(
  path: string,
  init: RequestInit,
): Promise<T> {
  const token = session.get();
  const resp = await fetch(`${ADMIN_API}${path}`, {
    ...init,
    headers: {
      "content-type": "application/json",
      accept: "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (resp.status === 401) {
    session.clear();
    throw new Error("unauthorized");
  }
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`${init.method ?? "GET"} ${path} failed: ${resp.status} ${text}`);
  }
  if (resp.status === 204) return {} as T;
  return (await resp.json()) as T;
}

export const adminApi = {
  async requestMagicLink(email: string) {
    await fetch(`${ADMIN_API}/auth/magic-link`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ email }),
    });
  },
  async verifyMagicLink(
    token: string,
  ): Promise<{ access_token: string; email: string; expires_at: number }> {
    const resp = await fetch(`${ADMIN_API}/auth/verify`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ token }),
    });
    if (!resp.ok) throw new Error("unauthorized");
    return resp.json();
  },
  openIncident(body: {
    title: string;
    body_markdown: string;
    severity: string;
    status: string;
    affected_components: string[];
  }) {
    return authedRequest<{ incident_id: string; version: string }>(
      "/admin/incidents",
      { method: "POST", body: JSON.stringify(body) },
    );
  },
  appendUpdate(
    incidentId: string,
    body: { status: string; body_markdown: string },
  ) {
    return authedRequest<{ incident_id: string; version: string }>(
      `/admin/incidents/${encodeURIComponent(incidentId)}/updates`,
      { method: "POST", body: JSON.stringify(body) },
    );
  },
  resolveIncident(incidentId: string, body_markdown: string) {
    return authedRequest<{ incident_id: string; version: string }>(
      `/admin/incidents/${encodeURIComponent(incidentId)}/resolve`,
      { method: "POST", body: JSON.stringify({ body_markdown }) },
    );
  },
  scheduleMaintenance(body: {
    title: string;
    body_markdown: string;
    scheduled_for: string;
    affected_components: string[];
    severity: string;
  }) {
    return authedRequest<{ incident_id: string; version: string }>(
      "/admin/maintenance",
      { method: "POST", body: JSON.stringify(body) },
    );
  },
};
